// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";

const ENABLE_LOCAL_OBSERVABILITY =
  process.env.NEXT_PUBLIC_ENABLE_LOCAL_OBSERVABILITY === "1";
const IS_PRODUCTION = process.env.NODE_ENV === "production";

const isLocalObservabilityHost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

if (!isLocalObservabilityHost || ENABLE_LOCAL_OBSERVABILITY) {
  Sentry.init({
    dsn: "https://a2413d6a327528aa8e293fbfb0a6cc6b@o4509487871229952.ingest.de.sentry.io/4509487872475216",

    // Keep full local diagnostics while limiting production work and event volume.
    tracesSampleRate: IS_PRODUCTION ? 0.1 : 1,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: IS_PRODUCTION ? 0.01 : 0.1,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,
  });

  // Replay buffers every visit when replaysOnErrorSampleRate is non-zero. Its
  // DOM/styles snapshot must not block the initial render. Keep error capture
  // active immediately, then load Replay in a separate chunk after page load.
  const loadReplay = () => {
    const addReplay = () => {
      void import("./lib/sentryReplay").then(({ createReplayIntegration }) => {
        if (!Sentry.getClient()?.getIntegrationByName("Replay")) {
          Sentry.addIntegration(createReplayIntegration());
        }
      }).catch(() => {
        // Optional diagnostics must not break the page if its chunk fails.
      });
    };
    window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(addReplay, { timeout: 5000 });
      } else {
        addReplay();
      }
    }, 2500);
  };
  if (document.readyState === "complete") loadReplay();
  else window.addEventListener("load", loadReplay, { once: true });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
